/* iter371 — Site Bar Prices page
 *
 * Centralised selling price management per site (and per audit
 * location for multi-loc sites).
 *
 * Layout:
 *   1. Site picker
 *   2. Readiness gate: blocker banner if Site Stocktake Settings missing
 *   3. Location selector (multi-loc only — multi-tick + copy-between)
 *   4. Header: "Effective from" date input + Save
 *   5. Subcategory pill filter
 *   6. Editable table (subcategory buckets, sorted alphabetically):
 *        Product | Previous (£ + effective_from) | Current (£ input)
 *
 * Carry-forward rule: blank `Current` rows are NO-OP — backend just
 * doesn't write a row. Existing price + date persist for that line.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteBarPricesService, sitesService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ArrowLeft, Save, Loader2, ShoppingCart, AlertTriangle, Copy, Search, X, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { getApiError } from '@/lib/utils';
import SitePicker from '../components/SitePicker';
import { subcategoryComparator } from '../lib/subcategoryOrder';

// Build a stable row-key for a (product, measure_ml) pair so React can
// reconcile add/remove of measure rows without losing focus.
const rowKey = (pid, ml) => `${pid}::${ml == null ? 'null' : ml}`;

// Pretty-print a unit_type ("bottle_330ml" → "Bottle 330ml").
const fmtMeasureLabel = (m) => {
  if (!m) return '';
  const lbl = m.measure_label || '';
  if (lbl.includes('_')) {
    const [head, ...rest] = lbl.split('_');
    return `${head.charAt(0).toUpperCase()}${head.slice(1)} ${rest.join(' ')}`;
  }
  return lbl ? `${lbl.charAt(0).toUpperCase()}${lbl.slice(1)}` : '';
};

// iter391 — Inline SVG line sparkline. Renders the last N price points
// (ascending chronological) as a single rose stroke with a dot on the
// most-recent point. Pure SVG, no external dep. Returns null when there
// is nothing meaningful to plot (≤1 point).
function PriceSparkline({ points, width = 96, height = 24, testId }) {
  if (!points || points.length < 2) {
    return (
      <span
        className="text-[10px] text-slate-300"
        data-testid={testId}
        title="Not enough history to plot a trend"
      >
        —
      </span>
    );
  }
  const ys = points.map(p => Number(p.price)).filter(n => !isNaN(n));
  if (ys.length < 2) return <span className="text-[10px] text-slate-300">—</span>;
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const span = maxY - minY || 1;  // avoid div/0 when flat
  const padX = 2;
  const padY = 3;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const xs = ys.map((_, i) => padX + (i * innerW) / (ys.length - 1));
  const yToCanvas = (y) => padY + innerH - ((y - minY) / span) * innerH;
  const pathD = xs.map((x, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${yToCanvas(ys[i]).toFixed(1)}`).join(' ');
  const lastX = xs[xs.length - 1];
  const lastY = yToCanvas(ys[ys.length - 1]);
  const first = ys[0];
  const last = ys[ys.length - 1];
  const direction = last > first ? '↑' : last < first ? '↓' : '→';
  const dirColor = last > first ? 'text-emerald-600' : last < first ? 'text-rose-600' : 'text-slate-400';
  const delta = last - first;
  const tooltip = points
    .map(p => `${p.effective_from}: £${Number(p.price).toFixed(2)}`)
    .join('\n') + `\nΔ ${delta >= 0 ? '+' : ''}£${delta.toFixed(2)}`;
  return (
    <span className="inline-flex items-center gap-1" data-testid={testId} title={tooltip}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
        aria-hidden="true"
      >
        <path
          d={pathD}
          fill="none"
          stroke="#e11d48"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={lastX} cy={lastY} r="2" fill="#e11d48" />
      </svg>
      <span className={`text-[10px] font-mono ${dirColor}`}>{direction}</span>
    </span>
  );
}

export default function SiteBarPricesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAdmin, isScheduler } = useAuth();

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pricesPayload, setPricesPayload] = useState(null);
  const [effectiveFrom, setEffectiveFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  // iter390 — Multi-measure editor state.
  // measureRowsByPid: { [pid]: [{ measure_ml, measure_label, current, _previous_price, _previous_effective_from }] }
  // Each entry represents one row in the editor. Empty `current` = carry-forward.
  const [measureRowsByPid, setMeasureRowsByPid] = useState({});
  const [search, setSearch] = useState('');
  const [activeSubcat, setActiveSubcat] = useState('all');
  // multi-loc state
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);  // for editing
  const [loadingLocId, setLoadingLocId] = useState(null);
  const [copySource, setCopySource] = useState('');  // source loc for copy

  // iter421 — Hold the latest measureRowsByPid in a ref so loadPrices can
  // safely preserve any in-flight typed values without depending on the
  // state (which would invalidate the callback identity and re-trigger
  // the auto-load effect, wiping the user's input).
  const measureRowsRef = useRef({});
  useEffect(() => { measureRowsRef.current = measureRowsByPid; }, [measureRowsByPid]);

  // iter421 — Guard the auto-load effect so it only runs once per
  // (siteId, location) pair. Without this guard, any stable-callback
  // miss (e.g., a toast re-render that rebuilt the useCallback) would
  // re-call loadPrices and clobber user-typed prices.
  const autoLoadedKeyRef = useRef(null);

  useEffect(() => {
    sitesService.list({ limit: 500 })
      .then(data => {
        const list = Array.isArray(data) ? data : data.sites || data.items || [];
        setSites(list);
        // Honour ?site=<id> deep link (e.g. from Wizard Sales CTA)
        const params = new URLSearchParams(window.location.search);
        const preselect = params.get('site');
        if (preselect && list.find(s => String(s.id) === preselect)) {
          setSelectedSiteId(preselect);
          reloadReadiness(preselect);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadReadiness = useCallback(async (siteId) => {
    if (!siteId) { setReadiness(null); return; }
    try {
      const r = await siteBarPricesService.getReadiness(siteId);
      setReadiness(r);
      // Reset selection when switching sites
      setSelectedLocationIds([]);
      setMeasureRowsByPid({});
      setPricesPayload(null);
      // iter421 — A fresh site means the autoLoad guard must be cleared
      // so the new site can load on the next effect tick.
      autoLoadedKeyRef.current = null;
    } catch (err) {
      toast({ title: 'Failed to load site', description: getApiError(err, 'Try again'), variant: 'destructive' });
    }
  }, [toast]);

  const handleSiteChange = (siteId) => {
    setSelectedSiteId(siteId);
    reloadReadiness(siteId);
  };

  // For single-loc: auto-load prices when site is ready
  // For multi-loc: load when the user picks a single location for editing
  const loadPrices = useCallback(async (loc = null) => {
    if (!selectedSiteId) return;
    setLoading(true);
    setLoadingLocId(loc);
    try {
      const data = await siteBarPricesService.getPrices(selectedSiteId, loc);
      setPricesPayload(data);
      // iter390 — Seed editor rows from the new payload shape.
      // - If product has existing prices → one row per existing measure.
      // - Else if product has exactly ONE master measure → auto-add that one row.
      // - Else show no rows; user clicks "Add Measure" to add.
      // iter421 — PRESERVE any in-flight typed values that the user has
      // entered but not yet saved. The previous behaviour blew away
      // these inputs every time the payload arrived, which is the root
      // cause of the "rapid-entry revert" — anything still in the
      // user's pipeline at reload time vanished.
      const priorRows = measureRowsRef.current || {};
      const seed = {};
      for (const p of (data?.products || [])) {
        const rows = [];
        const existing = p.existing_prices || [];
        const masters = p.master_measures || [];
        const prior = priorRows[p.beverage_product_id] || [];
        const priorByMl = new Map(prior.map(r => [r.measure_ml ?? 'null', r]));
        const pickTyped = (ml) => {
          const r = priorByMl.get(ml ?? 'null');
          return (r && r.current !== '' && r.current != null) ? String(r.current) : '';
        };
        if (existing.length > 0) {
          for (const e of existing) {
            // Resolve label from master if blank
            let label = e.measure_label;
            if (!label && e.measure_ml != null) {
              const m = masters.find(mm => mm.measure_ml === e.measure_ml);
              if (m) label = m.measure_label;
            }
            rows.push({
              measure_ml: e.measure_ml,
              measure_label: label || '',
              current: pickTyped(e.measure_ml),
              _previous_price: e.previous_price,
              _previous_effective_from: e.previous_effective_from,
              _history: e.history || [],
            });
          }
        } else if (masters.length === 1) {
          rows.push({
            measure_ml: masters[0].measure_ml,
            measure_label: masters[0].measure_label,
            current: pickTyped(masters[0].measure_ml),
            _previous_price: null,
            _previous_effective_from: null,
          });
        } else {
          // iter420 — Multi-measure with no existing prices: if the
          // product master has flagged a default sales unit, pre-seed
          // that one row. Falls back to empty (user picks via "+ Add
          // measure") when no default is set.
          const defaultSales = masters.find(m => m.is_default_sales_unit);
          if (defaultSales) {
            rows.push({
              measure_ml: defaultSales.measure_ml,
              measure_label: defaultSales.measure_label,
              current: pickTyped(defaultSales.measure_ml),
              _previous_price: null,
              _previous_effective_from: null,
            });
          }
        }
        // iter421 — Carry over any user-added measure rows that the
        // backend payload doesn't know about yet (e.g. "+ Add measure"
        // was clicked seconds before a reload landed).
        for (const r of prior) {
          if (!rows.some(rr => (rr.measure_ml ?? null) === (r.measure_ml ?? null))) {
            rows.push({ ...r });
          }
        }
        seed[p.beverage_product_id] = rows;
      }
      setMeasureRowsByPid(seed);
      // iter421 — Only refocus the first input on the *initial* load
      // for a (site, location). Subsequent reloads (e.g. after Save)
      // must not steal focus from a user mid-typing on another row.
      const key = `${selectedSiteId}::${loc ?? 'single'}`;
      if (autoLoadedKeyRef.current !== key) {
        focusedFirstRef.current = false;
        autoLoadedKeyRef.current = key;
      }
    } catch (err) {
      toast({ title: 'Failed to load prices', description: getApiError(err, 'Try again'), variant: 'destructive' });
    } finally {
      setLoading(false);
      setLoadingLocId(null);
    }
  }, [selectedSiteId, toast]);

  // Auto-load for single-loc sites
  // iter421 — Guard with autoLoadedKeyRef so an accidental remount or
  // callback-identity change cannot re-fire loadPrices and clobber
  // user-typed values mid-session.
  useEffect(() => {
    if (readiness && readiness.ready && !readiness.is_multi_loc) {
      const key = `${selectedSiteId}::single`;
      if (autoLoadedKeyRef.current === key) return;
      loadPrices(null);
    }
  }, [readiness, loadPrices, selectedSiteId]);

  const products = pricesPayload?.products || [];
  const subcats = useMemo(() => {
    const s = new Set();
    products.forEach(p => { if (p.subcategory) s.add(p.subcategory); });
    return Array.from(s).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    return products.filter(p => {
      if (activeSubcat !== 'all' && p.subcategory !== activeSubcat) return false;
      if (!q) return true;
      return (p.product_name || '').toLowerCase().includes(q)
        || (p.product_code || '').toLowerCase().includes(q)
        || (p.brand || '').toLowerCase().includes(q);
    });
  }, [products, search, activeSubcat]);

  // Group by subcategory for the table
  const buckets = useMemo(() => {
    const map = {};
    filtered.forEach(p => {
      const key = p.subcategory || '— Uncategorised —';
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return Object.entries(map).sort(([a], [b]) => subcategoryComparator(a, b));
  }, [filtered]);

  const filledCount = useMemo(() => {
    let n = 0;
    for (const rows of Object.values(measureRowsByPid)) {
      for (const r of rows) {
        if (r.current !== '' && r.current != null && !isNaN(Number(r.current))) n += 1;
      }
    }
    return n;
  }, [measureRowsByPid]);

  // iter374 — Site Bar Prices arrow key nav (↑/↓ between Current
  // inputs), mirroring the Sales-tab pattern. Bound on each input via
  // onKeyDown; the inputs are tagged with `data-bar-price-input` so
  // the query selector can build an ordered list at click time.
  const containerRef = useRef(null);
  const handlePriceKeyDown = (ev) => {
    // iter421 — ALWAYS preventDefault on ↑/↓ in the bar-price grid.
    // Without this guard, the browser's native number-input
    // increment/decrement (±0.01) fires when the user hits the boundary
    // input — manifesting as "the price just typed reverts to a slightly
    // different default value". Stopping the default unconditionally
    // makes the arrow keys behave purely as row-navigation.
    if (ev.key !== 'ArrowDown' && ev.key !== 'ArrowUp') return;
    ev.preventDefault();
    const root = containerRef.current || document;
    const inputs = Array.from(root.querySelectorAll('[data-bar-price-input]'));
    const cur = inputs.findIndex(el => el === ev.target);
    if (cur === -1) return;
    const next = ev.key === 'ArrowDown' ? inputs[cur + 1] : inputs[cur - 1];
    if (next) { next.focus(); try { next.select?.(); } catch { /* */ } }
  };
  const focusedFirstRef = useRef(false);
  useEffect(() => {
    if (!pricesPayload || loading) return;
    if (focusedFirstRef.current) return;
    // iter421 — Defer the auto-focus if the user is already typing in a
    // price input. Stealing focus mid-keystroke is the most direct way
    // to manifest the "values revert / disappear" race the user
    // reported.
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (active && active.matches?.('[data-bar-price-input]')) {
      focusedFirstRef.current = true;
      return;
    }
    const t = setTimeout(() => {
      const root = containerRef.current;
      if (!root) return;
      // Double-check at the timer tick — the user may have started
      // typing in the 120ms since the effect ran.
      const liveActive = document.activeElement;
      if (liveActive && liveActive.matches?.('[data-bar-price-input]')) {
        focusedFirstRef.current = true;
        return;
      }
      const first = root.querySelector('[data-bar-price-input]:not([disabled])');
      if (first) {
        first.focus();
        try { first.select?.(); } catch { /* */ }
        focusedFirstRef.current = true;
      }
    }, 120);
    return () => clearTimeout(t);
  }, [pricesPayload, loading]);

  const handleCopyToInputs = async () => {
    if (!copySource || selectedLocationIds.length === 0) {
      toast({ title: 'Pick source + at least one target', variant: 'destructive' });
      return;
    }
    try {
      const res = await siteBarPricesService.copyBetweenLocations(selectedSiteId, {
        from_location_id: Number(copySource),
        to_location_ids: selectedLocationIds.map(Number),
      });
      // iter390 — Distribute copied prices into the multi-measure
      // editor. Each draft is (pid, measure_ml, price). Add a row to
      // the product's bucket for the measure if not already present,
      // and populate its `current` price.
      setMeasureRowsByPid(prev => {
        const next = { ...prev };
        for (const d of (res.draft_prices || [])) {
          const pid = d.beverage_product_id;
          const ml = d.measure_ml;
          const rows = [...(next[pid] || [])];
          const existing = rows.findIndex(r => (r.measure_ml ?? null) === (ml ?? null));
          if (existing >= 0) {
            rows[existing] = { ...rows[existing], current: String(d.selling_price_incl_vat) };
          } else {
            // Try to resolve a label from the product's master measures
            const prod = (pricesPayload?.products || []).find(pp => pp.beverage_product_id === pid);
            const m = ml != null ? (prod?.master_measures || []).find(mm => mm.measure_ml === ml) : null;
            rows.push({
              measure_ml: ml,
              measure_label: m?.measure_label || '',
              current: String(d.selling_price_incl_vat),
              _previous_price: null,
              _previous_effective_from: null,
            });
          }
          next[pid] = rows;
        }
        return next;
      });
      toast({
        title: 'Draft loaded',
        description: `${res.count} prices copied — pick an effective_from date and Save.`,
      });
    } catch (err) {
      toast({ title: 'Copy failed', description: getApiError(err, 'Try again'), variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!effectiveFrom) {
      toast({ title: 'Pick an effective-from date', variant: 'destructive' });
      return;
    }
    if (readiness?.is_multi_loc && selectedLocationIds.length === 0) {
      toast({ title: 'Pick at least one location to save prices to', variant: 'destructive' });
      return;
    }
    // iter390 — Flatten measureRowsByPid into one PriceRow per (pid, measure_ml)
    // where the user typed a non-empty, numeric price.
    const prices = [];
    for (const [pid, rows] of Object.entries(measureRowsByPid)) {
      for (const r of rows) {
        if (r.current === '' || r.current == null || isNaN(Number(r.current))) continue;
        prices.push({
          beverage_product_id: Number(pid),
          selling_price_incl_vat: Number(r.current),
          measure_ml: r.measure_ml,
        });
      }
    }
    if (prices.length === 0) {
      toast({ title: 'Nothing to save', description: 'Fill at least one price before saving.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = { effective_from: effectiveFrom, prices };
      if (readiness?.is_multi_loc) body.audit_location_ids = selectedLocationIds.map(Number);
      const res = await siteBarPricesService.batchSave(selectedSiteId, body);
      toast({
        title: 'Prices saved',
        description: `${res.saved} price ${res.saved === 1 ? 'row' : 'rows'} written.`,
      });
      // iter421 — Clear the in-memory typed snapshot BEFORE the reload
      // so the preserve-typed-values guard in loadPrices doesn't
      // resurrect numbers that were just persisted (they'll come back
      // as `_previous_price` from the backend).
      measureRowsRef.current = {};
      // Reload to refresh "Previous" columns
      if (readiness?.is_multi_loc) {
        if (selectedLocationIds.length === 1) await loadPrices(selectedLocationIds[0]);
      } else {
        await loadPrices(null);
      }
    } catch (err) {
      toast({ title: 'Save failed', description: getApiError(err, 'Try again'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // iter390 — Per-row helpers for the multi-measure editor.
  const setMeasureCurrent = (pid, idx, value) => {
    setMeasureRowsByPid(prev => {
      const rows = [...(prev[pid] || [])];
      if (!rows[idx]) return prev;
      rows[idx] = { ...rows[idx], current: value };
      return { ...prev, [pid]: rows };
    });
  };

  const addMeasureRow = (pid, master) => {
    setMeasureRowsByPid(prev => {
      const rows = [...(prev[pid] || [])];
      // Prevent duplicates
      if (rows.some(r => (r.measure_ml ?? null) === (master.measure_ml ?? null))) return prev;
      rows.push({
        measure_ml: master.measure_ml,
        measure_label: master.measure_label,
        current: '',
        _previous_price: null,
        _previous_effective_from: null,
      });
      // Keep sorted by measure_ml ascending
      rows.sort((a, b) => (a.measure_ml ?? 0) - (b.measure_ml ?? 0));
      return { ...prev, [pid]: rows };
    });
  };

  const removeMeasureRow = (pid, idx) => {
    setMeasureRowsByPid(prev => {
      const rows = [...(prev[pid] || [])];
      rows.splice(idx, 1);
      return { ...prev, [pid]: rows };
    });
  };

  return (
    <div className="space-y-6" data-testid="site-bar-prices-page" ref={containerRef}>
      <Toaster />
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-rose-600" />
            Site Bar Prices
          </h1>
          <p className="text-sm text-muted-foreground">Selling prices (inc VAT) per site and audit location.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pick a site</CardTitle>
          <CardDescription className="text-xs">The site must have a Stocktake Settings default audit type configured before prices can be entered.</CardDescription>
        </CardHeader>
        <CardContent>
          <SitePicker
            sites={sites}
            value={selectedSiteId}
            onChange={handleSiteChange}
            placeholder="Search for a site..."
            testId="bar-prices-site-picker"
          />
        </CardContent>
      </Card>

      {selectedSiteId && readiness && !readiness.ready && (
        <Card className="border-amber-300 bg-amber-50" data-testid="bar-prices-readiness-blocker">
          <CardContent className="py-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-1">
                <p className="font-medium text-amber-900">Site is not ready for price entry</p>
                <ul className="text-xs text-amber-800 list-disc pl-4 space-y-0.5">
                  {readiness.missing.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400"
              onClick={() => navigate(`/stocktaking/site-settings?site=${selectedSiteId}`)}
              data-testid="bar-prices-config-cta"
            >
              Configure Site Stocktake Settings
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedSiteId && readiness?.ready && readiness.is_multi_loc && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pricing locations</CardTitle>
            <CardDescription className="text-xs">
              Tick one or more non-cellar locations to save the same price set to.
              You can also copy prices from one location to your selected targets.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {readiness.pricing_locations.map(loc => {
                const isSelected = selectedLocationIds.includes(loc.id);
                return (
                  <label
                    key={loc.id}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-sm ${isSelected ? 'border-rose-500 bg-rose-50' : 'border-slate-300 hover:bg-slate-50'}`}
                    data-testid={`bar-prices-loc-${loc.id}`}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(c) => {
                        setSelectedLocationIds(prev =>
                          c ? [...prev, loc.id] : prev.filter(id => id !== loc.id)
                        );
                      }}
                      data-testid={`bar-prices-loc-checkbox-${loc.id}`}
                    />
                    {loc.name}
                  </label>
                );
              })}
            </div>
            {selectedLocationIds.length === 1 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadPrices(selectedLocationIds[0])}
                disabled={loading}
                data-testid="bar-prices-load-single"
              >
                {loadingLocId === selectedLocationIds[0]
                  ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  : null}
                Load prices for {readiness.pricing_locations.find(l => l.id === selectedLocationIds[0])?.name}
              </Button>
            )}
            {selectedLocationIds.length > 1 && (
              <p className="text-xs text-muted-foreground" data-testid="bar-prices-bulk-hint">
                {selectedLocationIds.length} locations selected — the price set you enter below will save to all of them.
              </p>
            )}

            {/* Copy-between */}
            <div className="border-t pt-3 mt-3 space-y-2">
              <Label className="text-xs">Copy current effective prices into the form</Label>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="h-9 px-2 text-sm border rounded-md bg-white"
                  value={copySource}
                  onChange={e => setCopySource(e.target.value)}
                  data-testid="bar-prices-copy-source"
                >
                  <option value="">Source location…</option>
                  {readiness.pricing_locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyToInputs}
                  disabled={!copySource || selectedLocationIds.length === 0}
                  data-testid="bar-prices-copy-btn"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  Copy to selected
                </Button>
                <p className="text-[11px] text-muted-foreground">
                  Loads as a draft — pick an effective-from date below, then Save.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedSiteId && readiness?.ready && pricesPayload && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-base">Selling prices (inc VAT)</CardTitle>
                <CardDescription className="text-xs">
                  Blank rows carry forward the previous price and its effective date.
                  {' '}{filledCount} of {products.length} rows entered.
                  {' '}Typing a price (and pressing Tab/Enter to move on) does not save it —
                  nothing is committed until you click <span className="font-medium">Save prices</span> below.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div>
                  <Label className="text-[11px] text-muted-foreground">New effective from</Label>
                  <Input
                    type="date"
                    value={effectiveFrom}
                    onChange={e => setEffectiveFrom(e.target.value)}
                    className="h-8 w-40 text-xs"
                    data-testid="bar-prices-effective-from"
                  />
                </div>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || filledCount === 0}
                  data-testid="bar-prices-save"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save prices
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  placeholder="Search products…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  data-testid="bar-prices-search"
                />
                {search && (
                  <button
                    onClick={() => setSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    aria-label="Clear search"
                  >
                    <X className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                )}
              </div>
              <Badge
                variant={activeSubcat === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setActiveSubcat('all')}
                data-testid="bar-prices-subcat-all"
              >
                All ({products.length})
              </Badge>
              {subcats.map(sc => (
                <Badge
                  key={sc}
                  variant={activeSubcat === sc ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setActiveSubcat(sc)}
                  data-testid={`bar-prices-subcat-${sc}`}
                >
                  {sc}
                </Badge>
              ))}
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs" data-testid="bar-prices-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-left px-3 py-2 font-semibold w-40">Measure</th>
                    <th className="text-right px-3 py-2 font-semibold w-40">Previous (£)</th>
                    <th className="text-center px-3 py-2 font-semibold w-32">Trend</th>
                    <th className="text-right px-3 py-2 font-semibold w-32">Current (£)</th>
                    <th className="px-3 py-2 font-semibold w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {buckets.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">No products match.</td></tr>
                  )}
                  {buckets.map(([subcat, rows]) => (
                    <React.Fragment key={subcat}>
                      <tr className="bg-slate-100/60 border-t">
                        <td colSpan={6} className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                          {subcat} <span className="font-normal text-slate-500">· {rows.length}</span>
                        </td>
                      </tr>
                      {rows.map(p => {
                        const pid = p.beverage_product_id;
                        const measureRows = measureRowsByPid[pid] || [];
                        const masters = p.master_measures || [];
                        const usedMls = new Set(measureRows.map(r => r.measure_ml));
                        const availableMasters = masters.filter(m => !usedMls.has(m.measure_ml));
                        const rowSpan = Math.max(1, measureRows.length);

                        if (measureRows.length === 0) {
                          // Product with no rows yet (multi-master, none priced). Show an empty placeholder row.
                          return (
                            <tr
                              key={pid}
                              className="border-t hover:bg-slate-50/40"
                              data-testid={`bar-prices-row-${pid}`}
                            >
                              <td className="px-3 py-1.5 align-top">
                                <div className="font-medium">{p.product_name}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {p.product_code && <span className="font-mono mr-1">{p.product_code}</span>}
                                  {p.brand}
                                </div>
                                {p.default_unit_hint && (
                                  <div
                                    className="text-[10px] italic text-slate-500"
                                    data-testid={`bar-prices-unit-hint-${pid}`}
                                  >
                                    {p.default_unit_hint}
                                  </div>
                                )}
                              </td>
                              <td colSpan={4} className="px-3 py-2 text-[11px] text-slate-500">
                                No measure priced yet — add one from the master measures.
                              </td>
                              <td className="px-3 py-1.5">
                                {availableMasters.length > 0 && (
                                  <AddMeasureMenu
                                    pid={pid}
                                    masters={availableMasters}
                                    onAdd={(m) => addMeasureRow(pid, m)}
                                    isCountProduct={p.is_count_product}
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        }

                        return measureRows.map((mr, mi) => (
                          <tr
                            key={`${pid}::${mr.measure_ml == null ? 'null' : mr.measure_ml}`}
                            className="border-t hover:bg-slate-50/40"
                            data-testid={`bar-prices-row-${pid}-${mr.measure_ml == null ? 'null' : mr.measure_ml}`}
                          >
                            {mi === 0 ? (
                              <td className="px-3 py-1.5 align-top" rowSpan={rowSpan}>
                                <div className="font-medium">{p.product_name}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {p.product_code && <span className="font-mono mr-1">{p.product_code}</span>}
                                  {p.brand}
                                </div>
                                {p.default_unit_hint && (
                                  <div
                                    className="text-[10px] italic text-slate-500"
                                    data-testid={`bar-prices-unit-hint-${pid}`}
                                  >
                                    {p.default_unit_hint}
                                  </div>
                                )}
                              </td>
                            ) : null}
                            <td className="px-3 py-1.5">
                              <span className="text-xs font-medium" data-testid={`bar-prices-measure-${pid}-${mi}`}>
                                {/* iter420g — Backend now stamps a real measure_label
                                    on every existing-price row (via the
                                    effective_sales_unit resolver) so legacy
                                    null-ml rows pick up the canonical label.
                                    The "Default (legacy)" branch survives as
                                    a last-ditch hint for rows that arrive
                                    before the backfill catches up. */}
                                {fmtMeasureLabel(mr)
                                  || (p.effective_sales_unit?.unit_type
                                    ? p.effective_sales_unit.unit_type
                                    : (mr.measure_ml == null
                                      ? <span className="italic text-slate-500">Default (legacy)</span>
                                      : '—'))}
                              </span>
                              {mr.measure_ml != null && !p.is_count_product && (
                                <span className="text-[10px] text-slate-500 ml-1">({mr.measure_ml}ml)</span>
                              )}
                            </td>
                            <td className="text-right px-3 tabular-nums">
                              {mr._previous_price != null ? (
                                <>
                                  <div>£{Number(mr._previous_price).toFixed(2)}</div>
                                  <div className="text-[10px] text-muted-foreground">
                                    from {mr._previous_effective_from}
                                  </div>
                                </>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="text-center px-3">
                              <PriceSparkline
                                points={mr._history || []}
                                testId={`bar-prices-sparkline-${pid}-${mr.measure_ml == null ? 'null' : mr.measure_ml}`}
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                inputMode="decimal"
                                value={mr.current ?? ''}
                                onChange={e => setMeasureCurrent(pid, mi, e.target.value)}
                                onKeyDown={handlePriceKeyDown}
                                onWheel={e => e.target.blur()}
                                data-bar-price-input
                                placeholder={mr._previous_price != null ? Number(mr._previous_price).toFixed(2) : '0.00'}
                                className="h-7 text-right text-xs tabular-nums"
                                data-testid={`bar-prices-input-${pid}-${mr.measure_ml == null ? 'null' : mr.measure_ml}`}
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="flex items-center gap-1 justify-end">
                                {measureRows.length > 1 && mr._previous_price == null && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-6 w-6 text-red-500 hover:text-red-700"
                                    onClick={() => removeMeasureRow(pid, mi)}
                                    data-testid={`bar-prices-remove-${pid}-${mr.measure_ml == null ? 'null' : mr.measure_ml}`}
                                    title="Remove this measure row"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                                {mi === measureRows.length - 1 && availableMasters.length > 0 && (
                                  <AddMeasureMenu
                                    pid={pid}
                                    masters={availableMasters}
                                    onAdd={(m) => addMeasureRow(pid, m)}
                                    isCountProduct={p.is_count_product}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ));
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedSiteId && readiness?.ready && readiness.is_multi_loc && !pricesPayload && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-center text-sm text-muted-foreground" data-testid="bar-prices-multi-empty">
            Tick a location above and click <span className="font-medium">"Load prices"</span> to begin editing.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// iter390 — Lightweight inline "+ Add Measure" picker. iter420 — Now
// filters the dropdown to sales-tagged measures by default. A "Show all"
// link reveals count/purchase measures too (rare, e.g. promo bottle sale).
function AddMeasureMenu({ pid, masters, onAdd, isCountProduct }) {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  // iter420 — Default view = sales-tagged measures only. If none are
  // tagged (legacy data, edge cases), fall back to showing everything
  // so the user is never stuck.
  const salesOnly = masters.filter(m => m.is_sales_unit);
  const displayed = showAll || salesOnly.length === 0 ? masters : salesOnly;
  const hasNonSales = masters.length > salesOnly.length;
  return (
    <div className="relative inline-block" ref={ref}>
      <Button
        size="sm"
        variant="outline"
        className="h-6 text-[10px] px-1.5"
        onClick={() => setOpen(o => !o)}
        data-testid={`bar-prices-add-measure-btn-${pid}`}
      >
        <Plus className="h-3 w-3 mr-0.5" />
        Add measure
      </Button>
      {open && (
        <div
          className="absolute right-0 z-10 mt-1 w-52 rounded-md border border-slate-200 bg-white shadow-md p-1 text-xs"
          data-testid={`bar-prices-add-measure-menu-${pid}`}
        >
          {displayed.map(m => (
            <button
              key={`${m.measure_ml}-${m.measure_label}`}
              className="w-full text-left px-2 py-1 rounded hover:bg-slate-100 capitalize flex items-center justify-between"
              onClick={() => { onAdd(m); setOpen(false); }}
              data-testid={`bar-prices-add-measure-opt-${pid}-${m.measure_ml}`}
            >
              <span>
                {fmtMeasureLabel(m)}
                {!isCountProduct && (
                  <span className="text-[10px] text-slate-500 ml-1">({m.measure_ml}ml)</span>
                )}
              </span>
              {m.is_default_sales_unit && (
                <span className="text-[9px] uppercase text-rose-600 font-semibold ml-1">default</span>
              )}
            </button>
          ))}
          {salesOnly.length > 0 && hasNonSales && (
            <button
              className="w-full text-center px-2 py-1 mt-1 border-t border-slate-100 text-[10px] text-slate-500 hover:text-slate-700"
              onClick={() => setShowAll(s => !s)}
              data-testid={`bar-prices-add-measure-toggle-${pid}`}
            >
              {showAll ? 'Show sales measures only' : 'Show all measures (incl. count/purchase)'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
